import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

import { API_URL } from '../../config/api';
import { readUsersFromRealm, realmAvailable, upsertRealmDocs } from '../../realm';
import { normalizeUserRecord } from '../../utils/directMessageUtils';

// Async thunks
export const fetchUsers = createAsyncThunk(
  'users/fetchUsers',
  async (arg, { rejectWithValue }) => {
    const fromNetwork = Boolean(arg?.fromNetwork || arg?.force);
    const cached = await readUsersFromRealm();
    const offline = typeof navigator !== 'undefined' && navigator.onLine === false;

    try {
      if (await realmAvailable() && cached?.length && !fromNetwork) {
        return cached.map(normalizeUserRecord);
      }
      if (offline) {
        return (cached || []).map(normalizeUserRecord);
      }

      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const list = Array.isArray(response.data) ? response.data : [];
      upsertRealmDocs('UserProfile', list);
      const normalized = (list.length ? list : (cached || [])).map(normalizeUserRecord);
      return normalized;
    } catch (error) {
      if (cached && cached.length) {
        return cached.map(normalizeUserRecord);
      }
      return rejectWithValue(error.response?.data || 'Failed to fetch users');
    }
  },
  {
    condition: (arg, { getState }) => {
      const { users } = getState();
      const force = Boolean(arg?.force);

      if (force) {
        return true;
      }

      if (users.loading) {
        return false;
      }

      // Avoid repeated auto-fetching after an initial attempt.
      return !users.hasAttemptedFetch;
    },
  }
);

const initialState = {
  users: [],
  loading: false,
  error: null,
  hasAttemptedFetch: false,
  hasLoaded: false,
};

const userSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    updateUserStatusInList: (state, action) => {
      const { userId, status, workLocation, statusMessage, statusSource, onBreak } = action.payload;
      if (userId == null) return;
      const user = state.users.find((u) => String(u.id) === String(userId));
      if (user) {
        if (status != null) {
          user.status = typeof status === 'object'
            ? (status.name || status.status || status)
            : status;
        }
        if (workLocation !== undefined) user.workLocation = workLocation;
        if (statusMessage !== undefined) user.statusMessage = statusMessage;
        if (statusSource !== undefined) user.statusSource = statusSource;
        if (onBreak !== undefined) user.onBreak = onBreak === true;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload;
        state.error = null;
        state.hasAttemptedFetch = true;
        state.hasLoaded = true;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.hasAttemptedFetch = true;
      });
  },
});

export const { clearError, updateUserStatusInList } = userSlice.actions;
export default userSlice.reducer;
